import type { NovelParagraphEntity, ParagraphTextBlock } from "~/src/api";
import { getNovelParagraphImage } from "~/src/api";
import cloneDeep from "clone-deep";

export interface TextBlock extends ParagraphTextBlock {
  // used for locating bookmarks/highlights
  paragraphId?: string;
  imageVariants?: NonNullable<
    NovelParagraphEntity["attributes"]["imageVariants"]
  >;
  footnoteMeta?: {
    // id in the form of <paragraphId=global_num>
    id: string;
    text: string;
  };
}

const scaleMap: Record<string, number> = {
  "-2": 0.67,
  "-1": 0.83,
  "0": 1,
  "1": 1.17,
  "2": 1.5,
  "3": 2,
};

export const fontScaleLevelToRatio = (scaleLevel?: number) => {
  if (!scaleLevel) return undefined;
  return scaleMap[scaleLevel.toString()];
};

export const isTextBlock = (block: TextBlock | string): block is TextBlock => {
  return !(typeof block === "string");
};

export const getContent = (block: TextBlock | string): string | TextBlock[] => {
  if (isTextBlock(block)) {
    if (block.type === "scene_break") return [];

    if (Array.isArray(block.content)) {
      return block.content;
    }

    return block.content;
  }
  return block;
};

export const getImageSrc = (block: TextBlock): string => {
  const content = getContent(block);
  if (typeof content === "string") return content;
  return "";
};

export const paragraphEntityToTextBlock = (
  paragraph: NovelParagraphEntity,
  res: "source" | "high" | "medium" | "low" = "source",
): TextBlock => {
  if (paragraph.attributes.imageVariants) {
    return {
      ...paragraph.attributes.text,
      paragraphId: paragraph.id,
      type: "image",
      imageVariants: {
        SOURCE: getNovelParagraphImage(paragraph)!,
        HIGH: getNovelParagraphImage(paragraph, "1920")!,
        MEDIUM: getNovelParagraphImage(paragraph, "1280")!,
        LOW: getNovelParagraphImage(paragraph, "640")!,
      },
    };
  }

  return {
    paragraphId: paragraph.id,
    ...paragraph.attributes.text,
  };
};

// this is functionally identical to the map function in render
export const augmentWithFootnoteIds = (input: TextBlock[]) => {
  const cloned = cloneDeep(input);
  let globalFootnoteNum = 1;

  // we're assuming all top level blocks have some sort of paragraph id
  for (const block of cloned) {
    const currentParaId = block.paragraphId ?? "<unknown>";

    // inorder n-ary tree traversal
    const traverseChildFootnotes = (node: TextBlock) => {
      const content = getContent(node);
      if (Array.isArray(content))
        content.forEach((child) => traverseChildFootnotes(child));

      // we add it after the fact since footnote numbers are at the end of the node
      if (node.footnote) {
        node.footnoteMeta = {
          id: `${currentParaId}=${globalFootnoteNum++}`,
          text: node.footnote,
        };
      }
    };

    const content = getContent(block);
    if (Array.isArray(content))
      content.forEach((child) => traverseChildFootnotes(child));

    if (block.footnote) {
      block.footnoteMeta = {
        id: `${currentParaId}=${globalFootnoteNum++}`,
        text: block.footnote,
      };
    }
  }

  return cloned;
};

export function getFootnoteMap(input: string | TextBlock[]) {
  // ids (in the form of <paragraphId#number>) to text
  const footnoteMap: Map<string, string> = new Map();
  if (typeof input === "string") return footnoteMap;
  let globalFootnoteNum = 1;

  // we're assuming all top level blocks have some sort of paragraph id
  for (const block of input) {
    const currentParaId = block.paragraphId ?? "<unknown>";

    // inorder n-ary tree traversal
    const traverseChildFootnotes = (node: TextBlock) => {
      const content = getContent(node);
      if (Array.isArray(content))
        content.forEach((child) => traverseChildFootnotes(child));

      // we add it after the fact since footnote numbers are at the end of the node
      if (node.footnote)
        footnoteMap.set(
          `${currentParaId}=${globalFootnoteNum++}`,
          node.footnote,
        );
    };

    const content = getContent(block);
    if (Array.isArray(content))
      content.forEach((child) => traverseChildFootnotes(child));

    if (block.footnote)
      footnoteMap.set(
        `${currentParaId}=${globalFootnoteNum++}`,
        block.footnote,
      );
  }

  return footnoteMap;
}
